import { useEffect, useRef, useState } from 'react';
import { GameProps } from './types';

type DinoPhase = 'intro' | 'playing' | 'gameover';
type ObstacleType = 'saguaro' | 'barrel' | 'rock' | 'tumbleweed';

interface Obstacle {
  x: number;
  width: number;
  height: number;
  type: ObstacleType;
}

interface Cloud {
  x: number;
  y: number;
  width: number;
  speed: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// 8-bit dino sprite (12x14 pixels) - T-Rex style
// 0 = transparent, 1 = body, 2 = eye
const DINO_SPRITE = [
  [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 0],
  [0, 0, 0, 0, 0, 1, 2, 1, 1, 1, 1, 1],
  [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1],
  [0, 0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0],
  [1, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
  [1, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  [0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0],
];

const DINO_SPRITE_WIDTH = 12;
const DINO_SPRITE_HEIGHT = 14;

// Saguaro cactus sprite (7x16)
const SAGUARO_SPRITE = [
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 1, 0],
  [1, 0, 0, 1, 0, 1, 0],
  [1, 0, 0, 1, 0, 1, 0],
  [1, 1, 0, 1, 0, 1, 0],
  [0, 1, 0, 1, 1, 1, 0],
  [0, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0],
];

// Barrel cactus sprite (6x6)
const BARREL_SPRITE = [
  [0, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1],
  [0, 1, 1, 1, 1, 0],
];

// Rock sprite (8x5)
const ROCK_SPRITE = [
  [0, 0, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [0, 1, 1, 1, 1, 1, 1, 0],
];

// Tumbleweed sprite (7x7) - circular spiky
const TUMBLEWEED_SPRITE = [
  [0, 0, 1, 1, 1, 0, 0],
  [0, 1, 0, 1, 0, 1, 0],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 1, 1, 1, 1, 1, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [0, 1, 0, 1, 0, 1, 0],
  [0, 0, 1, 1, 1, 0, 0],
];

// Vegas skyline silhouette heights (relative, will be scaled)
const VEGAS_SKYLINE = [
  12, 14, 12, 10, 8, 10, 35, 32, 28, 15, 12, 18, 45, 42, 38, 20,
  15, 12, 10, 8, 25, 55, 50, 45, 30, 18, 12, 10, 8, 12, 15, 10,
];

// Desert mountains (background layers)
const MOUNTAIN_LAYER_1 = [0, 15, 35, 55, 70, 60, 45, 30, 50, 75, 90, 80, 55, 40, 25, 10, 0];
const MOUNTAIN_LAYER_2 = [0, 10, 25, 40, 50, 35, 45, 65, 55, 40, 30, 20, 35, 45, 30, 15, 0];

const drawSprite = (
  context: CanvasRenderingContext2D,
  sprite: number[][],
  x: number,
  y: number,
  width: number,
  height: number,
  color: string
) => {
  const rows = sprite.length;
  const cols = sprite[0].length;
  const pixelW = width / cols;
  const pixelH = height / rows;

  context.fillStyle = color;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (sprite[row][col] === 1) {
        context.fillRect(
          Math.floor(x + col * pixelW),
          Math.floor(y + row * pixelH),
          Math.ceil(pixelW),
          Math.ceil(pixelH)
        );
      }
    }
  }
};

const drawDino = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  bodyColor: string,
  eyeColor: string
) => {
  const pixelW = width / DINO_SPRITE_WIDTH;
  const pixelH = height / DINO_SPRITE_HEIGHT;

  for (let row = 0; row < DINO_SPRITE_HEIGHT; row++) {
    for (let col = 0; col < DINO_SPRITE_WIDTH; col++) {
      const pixel = DINO_SPRITE[row][col];
      if (pixel === 0) continue;

      context.fillStyle = pixel === 2 ? eyeColor : bodyColor;
      context.fillRect(
        Math.floor(x + col * pixelW),
        Math.floor(y + row * pixelH),
        Math.ceil(pixelW),
        Math.ceil(pixelH)
      );
    }
  }
};

export function DinoGame({ fgColor, bgColor, softColor, onExit }: GameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const stateRef = useRef({
    phase: 'intro' as DinoPhase,
    width: 0,
    height: 0,
    deviceRatio: 1,
    groundY: 0,
    dinoX: 52,
    dinoY: 0,
    dinoWidth: 28,
    dinoHeight: 36,
    velocityY: 0,
    obstacles: [] as Obstacle[],
    clouds: [] as Cloud[],
    spawnTimer: 0,
    spawnDelay: 1.2,
    speed: 260,
    score: 0,
    lastTime: 0,
    skylineOffset: 0,
  });

  const resetRound = () => {
    const state = stateRef.current;
    state.phase = 'intro';
    state.velocityY = 0;
    state.obstacles = [];
    state.spawnTimer = 0;
    state.spawnDelay = 1.2;
    state.speed = 260;
    state.score = 0;
    state.dinoY = state.groundY - state.dinoHeight;
    state.lastTime = 0;
    state.skylineOffset = 0;
    // Initialize some clouds
    state.clouds = [];
    for (let i = 0; i < 4; i++) {
      state.clouds.push({
        x: Math.random() * state.width,
        y: 40 + Math.random() * 40,
        width: 30 + Math.random() * 40,
        speed: 15 + Math.random() * 25,
      });
    }
  };

  const resizeCanvas = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    const width = Math.max(260, Math.floor(bounds.width));
    const height = Math.max(180, Math.floor(bounds.height));
    const deviceRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * deviceRatio);
    canvas.height = Math.floor(height * deviceRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const state = stateRef.current;
    state.width = width;
    state.height = height;
    state.deviceRatio = deviceRatio;
    state.groundY = height - 26;
    state.dinoHeight = clamp(Math.floor(height * 0.14), 30, 42);
    state.dinoWidth = clamp(Math.floor(height * 0.1), 24, 34);
    state.dinoX = clamp(Math.floor(width * 0.08), 42, 78);
    state.dinoY = state.groundY - state.dinoHeight;

    resetRound();
  };

  const jump = () => {
    const state = stateRef.current;
    if (state.dinoY >= state.groundY - state.dinoHeight - 1) {
      state.velocityY = -620;
    }
  };

  const handleTouchAction = () => {
    const state = stateRef.current;

    if (state.phase === 'intro') {
      state.phase = 'playing';
      jump();
      return;
    }

    if (state.phase === 'playing') {
      jump();
      return;
    }

    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
      jump();
    }
  };

  const handleTouchQuit = () => {
    onExit(String(Math.floor(stateRef.current.score)));
  };

  const update = (timestamp: number) => {
    const state = stateRef.current;
    if (!state.lastTime) {
      state.lastTime = timestamp;
    }

    const delta = clamp((timestamp - state.lastTime) / 1000, 0, 0.034);
    state.lastTime = timestamp;

    if (state.phase !== 'playing') {
      return;
    }

    state.score += delta * 10;
    state.speed = clamp(260 + state.score * 2.6, 260, 520);

    state.velocityY += 1700 * delta;
    state.dinoY += state.velocityY * delta;
    if (state.dinoY > state.groundY - state.dinoHeight) {
      state.dinoY = state.groundY - state.dinoHeight;
      state.velocityY = 0;
    }

    state.spawnTimer += delta;
    if (state.spawnTimer >= state.spawnDelay) {
      state.spawnTimer = 0;
      state.spawnDelay = 0.75 + Math.random() * 1.05;

      const obstacleTypes: ObstacleType[] = ['saguaro', 'barrel', 'rock', 'tumbleweed'];
      const type = obstacleTypes[Math.floor(Math.random() * obstacleTypes.length)];

      let width: number;
      let height: number;
      switch (type) {
        case 'saguaro':
          width = clamp(18 + Math.random() * 10, 18, 28);
          height = clamp(45 + Math.random() * 20, 45, 65);
          break;
        case 'barrel':
          width = clamp(20 + Math.random() * 10, 20, 30);
          height = clamp(20 + Math.random() * 8, 20, 28);
          break;
        case 'rock':
          width = clamp(24 + Math.random() * 16, 24, 40);
          height = clamp(16 + Math.random() * 10, 16, 26);
          break;
        case 'tumbleweed':
          const size = clamp(22 + Math.random() * 12, 22, 34);
          width = size;
          height = size;
          break;
      }

      state.obstacles.push({ x: state.width + 10, width, height, type });
    }

    // Update clouds
    state.clouds = state.clouds.map((cloud) => ({
      ...cloud,
      x: cloud.x - cloud.speed * delta,
    }));
    // Respawn clouds that go off screen
    state.clouds.forEach((cloud) => {
      if (cloud.x + cloud.width < 0) {
        cloud.x = state.width + Math.random() * 100;
        cloud.y = 40 + Math.random() * 40;
        cloud.width = 30 + Math.random() * 40;
        cloud.speed = 15 + Math.random() * 25;
      }
    });

    // Update skyline parallax
    state.skylineOffset = (state.skylineOffset + state.speed * delta * 0.1) % (state.width * 2);

    state.obstacles = state.obstacles
      .map((obstacle) => ({ ...obstacle, x: obstacle.x - state.speed * delta }))
      .filter((obstacle) => obstacle.x + obstacle.width > -10);

    const dinoLeft = state.dinoX;
    const dinoRight = state.dinoX + state.dinoWidth;
    const dinoTop = state.dinoY;
    const dinoBottom = state.dinoY + state.dinoHeight;

    const collided = state.obstacles.some((obstacle) => {
      const obstacleLeft = obstacle.x;
      const obstacleRight = obstacle.x + obstacle.width;
      const obstacleTop = state.groundY - obstacle.height;
      const obstacleBottom = state.groundY;
      return dinoLeft < obstacleRight && dinoRight > obstacleLeft && dinoTop < obstacleBottom && dinoBottom > obstacleTop;
    });

    if (collided) {
      state.phase = 'gameover';
    }
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const state = stateRef.current;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(state.deviceRatio, 0, 0, state.deviceRatio, 0, 0);

    context.fillStyle = bgColor;
    context.fillRect(0, 0, state.width, state.height);

    // Draw distant mountains (layer 2 - furthest)
    context.globalAlpha = 0.08;
    context.fillStyle = fgColor;
    const mountainBaseY = state.groundY - 20;
    const mt2Width = state.width / (MOUNTAIN_LAYER_2.length - 1);
    context.beginPath();
    context.moveTo(0, mountainBaseY);
    MOUNTAIN_LAYER_2.forEach((h, i) => {
      context.lineTo(i * mt2Width, mountainBaseY - h * 0.8);
    });
    context.lineTo(state.width, mountainBaseY);
    context.closePath();
    context.fill();

    // Draw mountains (layer 1 - closer)
    context.globalAlpha = 0.12;
    const mt1Width = state.width / (MOUNTAIN_LAYER_1.length - 1);
    context.beginPath();
    context.moveTo(0, mountainBaseY);
    MOUNTAIN_LAYER_1.forEach((h, i) => {
      context.lineTo(i * mt1Width, mountainBaseY - h * 0.7);
    });
    context.lineTo(state.width, mountainBaseY);
    context.closePath();
    context.fill();

    // Draw Vegas skyline silhouette (parallax scrolling)
    context.globalAlpha = 0.15;
    const skylineY = state.groundY - 10;
    const buildingWidth = 16;
    const skylineFullWidth = VEGAS_SKYLINE.length * buildingWidth;
    const skylineX = -((state.skylineOffset * 0.3) % skylineFullWidth);

    for (let offset = skylineX; offset < state.width + buildingWidth; offset += skylineFullWidth) {
      VEGAS_SKYLINE.forEach((h, i) => {
        const bx = offset + i * buildingWidth;
        if (bx > -buildingWidth && bx < state.width + buildingWidth) {
          context.fillRect(bx, skylineY - h, buildingWidth - 2, h);
          // Add a small spire/antenna on taller buildings
          if (h > 40) {
            context.fillRect(bx + buildingWidth / 2 - 1, skylineY - h - 8, 2, 8);
          }
        }
      });
    }
    context.globalAlpha = 1;

    // Draw clouds
    context.globalAlpha = 0.15;
    context.fillStyle = fgColor;
    state.clouds.forEach((cloud) => {
      // Simple puffy cloud shape
      const cw = cloud.width;
      const ch = cw * 0.4;
      context.beginPath();
      context.arc(cloud.x + cw * 0.25, cloud.y + ch * 0.6, ch * 0.5, 0, Math.PI * 2);
      context.arc(cloud.x + cw * 0.5, cloud.y + ch * 0.4, ch * 0.6, 0, Math.PI * 2);
      context.arc(cloud.x + cw * 0.75, cloud.y + ch * 0.6, ch * 0.5, 0, Math.PI * 2);
      context.fill();
    });
    context.globalAlpha = 1;

    // Score display
    context.fillStyle = fgColor;
    context.font = '16px "Courier New", monospace';
    context.fillText(`DINO  SCORE ${Math.floor(state.score)}`, 8, 22);

    // Ground texture - desert dots
    context.globalAlpha = 0.2;
    context.fillStyle = fgColor;
    for (let x = 0; x < state.width; x += 24) {
      context.fillRect(x, state.groundY + 2, 15, 2);
    }
    // Add some scattered desert pebbles
    for (let x = 0; x < state.width; x += 40) {
      context.fillRect(x + 8, state.groundY + 6, 3, 2);
      context.fillRect(x + 20, state.groundY + 8, 2, 2);
    }
    context.globalAlpha = 1;

    // Ground line
    context.strokeStyle = fgColor;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, state.groundY);
    context.lineTo(state.width, state.groundY);
    context.stroke();

    // Draw dino
    drawDino(context, state.dinoX, state.dinoY, state.dinoWidth, state.dinoHeight, fgColor, bgColor);

    // Draw obstacles based on type
    state.obstacles.forEach((obstacle) => {
      const obstacleY = state.groundY - obstacle.height;
      switch (obstacle.type) {
        case 'saguaro':
          drawSprite(context, SAGUARO_SPRITE, obstacle.x, obstacleY, obstacle.width, obstacle.height, softColor);
          break;
        case 'barrel':
          drawSprite(context, BARREL_SPRITE, obstacle.x, obstacleY, obstacle.width, obstacle.height, softColor);
          break;
        case 'rock':
          drawSprite(context, ROCK_SPRITE, obstacle.x, obstacleY, obstacle.width, obstacle.height, softColor);
          break;
        case 'tumbleweed':
          drawSprite(context, TUMBLEWEED_SPRITE, obstacle.x, obstacleY, obstacle.width, obstacle.height, softColor);
          break;
      }
    });

    if (state.phase === 'intro') {
      context.fillStyle = softColor;
      context.font = '15px "Courier New", monospace';
      context.fillText('Press SPACE or UP to jump. Press ENTER to start. Press ESC to quit.', 8, state.height - 12);
    }

    if (state.phase === 'gameover') {
      context.fillStyle = 'rgba(0, 0, 0, 0.6)';
      context.fillRect(0, 0, state.width, state.height);
      context.fillStyle = fgColor;
      context.font = '24px "Courier New", monospace';
      context.fillText('GAME OVER', Math.max(8, state.width / 2 - 72), state.height / 2 - 10);
      context.font = '16px "Courier New", monospace';
      context.fillStyle = softColor;
      context.fillText(`Score: ${Math.floor(state.score)}`, Math.max(8, state.width / 2 - 50), state.height / 2 + 18);
      context.fillText('Press ENTER to restart or ESC to quit', Math.max(8, state.width / 2 - 158), state.height / 2 + 44);
    }
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    resizeCanvas();

    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    const tick = (timestamp: number) => {
      update(timestamp);
      draw();
      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);

    const handleKeyDown = (event: KeyboardEvent) => {
      const state = stateRef.current;
      const key = event.key;

      if (key === 'Escape') {
        event.preventDefault();
        onExit(String(Math.floor(state.score)));
        return;
      }

      const jumpPressed = key === ' ' || key === 'Spacebar' || key === 'ArrowUp';
      if (jumpPressed) {
        event.preventDefault();
      }

      if (state.phase === 'intro' && (key === 'Enter' || jumpPressed)) {
        state.phase = 'playing';
        if (jumpPressed) {
          jump();
        }
        return;
      }

      if (state.phase === 'playing' && jumpPressed) {
        jump();
      }

      if (state.phase === 'gameover' && key === 'Enter') {
        event.preventDefault();
        resetRound();
        state.phase = 'playing';
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="game-canvas-wrap" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="game-canvas"
        onTouchStart={(event) => {
          event.preventDefault();
          handleTouchAction();
        }}
        onTouchMove={(event) => event.preventDefault()}
      />
      {isTouchDevice ? (
        <button
          className="touch-btn touch-quit-btn dino-touch-quit-btn"
          type="button"
          onTouchStart={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleTouchQuit();
          }}
          onClick={handleTouchQuit}
          aria-label="Quit game"
        >
          ✕ QUIT
        </button>
      ) : null}
    </div>
  );
}
